/**
 * Centralized demo data for ?demo=true mode
 * This provides realistic data for all pages and components
 */

// Demo brand name - use this instead of 'Your Brand' in demo mode
export const DEMO_BRAND_NAME = 'Acme Snacks'
export const DEMO_BRAND_DOMAIN = 'acme-snacks.com'

// Dashboard - Brand Performance Card
export const demoBrandPerformance = {
  id: 'demo-brand-1',
  name: 'Acme Snacks',
  domain: 'acme-snacks.com',
  aiIndexScore: 73.5,
  mentionRate: 58.2,
  hasLlms: true,
  hasManifest: true,
  queriesAnalyzed: 156,
  baseScore: 68,
  breakdown: {
    baseScore: 68,
    visibility: {
      details: {
        mentionRate: 0.582
      }
    },
    details: {
      trackedCompetitors: 5,
      contentAccessible: true,
      hasLlmsTxt: true,
      hasManifest: true
    }
  }
}

// Dashboard - Share of Voice Card
export const demoShareOfVoice = {
  data: [
    {
      id: 'demo-brand-1',
      name: 'Acme Snacks',
      isYourBrand: true,
      shareOfVoice: 28.5,
      mentions: 87,
      totalMentions: 305,
      providers: ['OPENAI', 'ANTHROPIC', 'GOOGLE']
    },
    {
      id: 'competitor-1',
      name: 'NatureBox',
      isYourBrand: false,
      shareOfVoice: 32.1,
      mentions: 98,
      totalMentions: 305,
      providers: ['OPENAI', 'ANTHROPIC', 'GOOGLE']
    },
    {
      id: 'competitor-2',
      name: 'Graze',
      isYourBrand: false,
      shareOfVoice: 24.3,
      mentions: 74,
      totalMentions: 305,
      providers: ['OPENAI', 'GOOGLE']
    },
    {
      id: 'competitor-3',
      name: 'Daily Harvest',
      isYourBrand: false,
      shareOfVoice: 15.1,
      mentions: 46,
      totalMentions: 305,
      providers: ['ANTHROPIC']
    }
  ]
}

// Dashboard - Sentiment Card
export const demoSentiment = {
  data: [
    {
      id: 'demo-brand-1',
      name: 'Acme Snacks',
      isYourBrand: true,
      averageScore: 72.3,
      totalMentions: 87,
      favorable: 68.5,
      neutral: 24.2,
      unfavorable: 7.3
    },
    {
      id: 'competitor-1',
      name: 'NatureBox',
      isYourBrand: false,
      averageScore: 78.1,
      totalMentions: 98,
      favorable: 74.2,
      neutral: 21.5,
      unfavorable: 4.3
    },
    {
      id: 'competitor-2',
      name: 'Graze',
      isYourBrand: false,
      averageScore: 65.8,
      totalMentions: 74,
      favorable: 62.1,
      neutral: 28.3,
      unfavorable: 9.6
    },
    {
      id: 'competitor-3',
      name: 'Daily Harvest',
      isYourBrand: false,
      averageScore: 58.2,
      totalMentions: 46,
      favorable: 54.3,
      neutral: 32.8,
      unfavorable: 12.9
    }
  ]
}

// Dashboard - Position Card
export const demoPosition = {
  data: [
    {
      id: 'demo-brand-1',
      name: 'Acme Snacks',
      isYourBrand: true,
      avgPosition: 2.8,
      totalMentions: 87
    },
    {
      id: 'competitor-1',
      name: 'NatureBox',
      isYourBrand: false,
      avgPosition: 1.9,
      totalMentions: 98
    },
    {
      id: 'competitor-2',
      name: 'Graze',
      isYourBrand: false,
      avgPosition: 3.5,
      totalMentions: 74
    },
    {
      id: 'competitor-3',
      name: 'Daily Harvest',
      isYourBrand: false,
      avgPosition: 4.2,
      totalMentions: 46
    }
  ]
}

// Dashboard - Top Competitors
export const demoCompetitorScores = [
  {
    id: 'competitor-1',
    name: 'NatureBox',
    domain: 'naturebox.com',
    aiIndexScore: 81.2,
    mentionRate: 64.1,
    avgPosition: 1.9,
    trend: 'up' as const,
    scoreChange: 5.2,
    hasLlms: true,
    hasManifest: true,
    hasWebhooks: true,
    queriesAnalyzed: 153,
    totalMentions: 98,
    recentMentions: 32,
    lastUpdated: new Date().toISOString()
  },
  {
    id: 'competitor-2',
    name: 'Graze',
    domain: 'graze.com',
    aiIndexScore: 69.5,
    mentionRate: 48.4,
    avgPosition: 3.5,
    trend: 'stable' as const,
    scoreChange: 0,
    hasLlms: true,
    hasManifest: false,
    hasWebhooks: false,
    queriesAnalyzed: 153,
    totalMentions: 74,
    recentMentions: 18,
    lastUpdated: new Date().toISOString()
  },
  {
    id: 'competitor-3',
    name: 'Daily Harvest',
    domain: 'daily-harvest.com',
    aiIndexScore: 62.8,
    mentionRate: 30.1,
    avgPosition: 4.2,
    trend: 'down' as const,
    scoreChange: -3.5,
    hasLlms: false,
    hasManifest: true,
    hasWebhooks: false,
    queriesAnalyzed: 153,
    totalMentions: 46,
    recentMentions: 12,
    lastUpdated: new Date().toISOString()
  }
]

// Dashboard Stats
export const demoDashboardStats = {
  hasLlmsTxt: true,
  trackedQueries: 156,
  dataHistoryMonths: 6,
  aiProviders: ['OpenAI', 'Anthropic', 'Google'],
  hasAnalytics: false,
  hasCRM: false,
  hasRevenue: false,
  contentCount: 42,
  enrichedContentCount: 38
}

// Search Intelligence - Overview Data
export const demoIntelligenceOverview = {
  totalQueries: 156,
  classifiedQueries: 142,
  queriesUserMentioned: 87,
  queriesCompetitorMentioned: 112,
  contentGapQueries: 34,
  topicsCovered: 6,
  userMentionRate: '55.8',
  competitorMentionRate: '71.8',
  intentBreakdown: [
    {
      intentType: 'Discovery',
      description: 'User is learning about options and exploring solutions',
      queryCount: 54,
      percentage: '34.6',
      avgCommercialIntent: '42'
    },
    {
      intentType: 'Comparison',
      description: 'User is comparing multiple solutions or alternatives',
      queryCount: 38,
      percentage: '24.4',
      avgCommercialIntent: '68'
    },
    {
      intentType: 'Purchase Intent',
      description: 'Ready to buy, transactional queries',
      queryCount: 28,
      percentage: '17.9',
      avgCommercialIntent: '85'
    },
    {
      intentType: 'Problem Solving',
      description: 'Specific problem to solve, troubleshooting',
      queryCount: 22,
      percentage: '14.1',
      avgCommercialIntent: '55'
    },
    {
      intentType: 'Research',
      description: 'Deep dive, statistics, case studies',
      queryCount: 14,
      percentage: '9.0',
      avgCommercialIntent: '38'
    }
  ],
  contentOpportunities: [
    {
      id: '1',
      query: 'What are the healthiest organic snack brands for kids?',
      topic: {
        name: 'Product Comparison',
        slug: 'products',
        icon: '🍎'
      },
      commercialIntent: 78,
      intentType: 'Comparison',
      competitorCount: 4
    },
    {
      id: '2',
      query: 'How much do organic snack subscriptions cost per month?',
      topic: {
        name: 'Pricing & Plans',
        slug: 'pricing',
        icon: '💰'
      },
      commercialIntent: 82,
      intentType: 'Purchase Intent',
      competitorCount: 3
    },
    {
      id: '3',
      query: 'Which snack brands have the best ingredient transparency?',
      topic: {
        name: 'Ingredients & Nutrition',
        slug: 'nutrition',
        icon: '🥗'
      },
      commercialIntent: 65,
      intentType: 'Comparison',
      competitorCount: 5
    },
    {
      id: '4',
      query: 'What certifications do organic snack brands have?',
      topic: {
        name: 'Certifications & Standards',
        slug: 'certifications',
        icon: '✓'
      },
      commercialIntent: 58,
      intentType: 'Research',
      competitorCount: 3
    },
    {
      id: '5',
      query: 'Are organic snack subscriptions worth the cost?',
      topic: {
        name: 'Value & Benefits',
        slug: 'value',
        icon: '⭐'
      },
      commercialIntent: 72,
      intentType: 'Discovery',
      competitorCount: 4
    }
  ]
}

// Search Intelligence - Query Objectives
export const demoQueryObjectives = [
  {
    objective_slug: 'discovery',
    objective_name: 'Discovery',
    description: 'Learning about options, exploring solutions',
    query_count: 45,
    avg_confidence: 0.85,
    your_mentions: 28,
    percentage: '35.0',
    your_mention_rate: '62.2',
    example_queries: [
      'What are the best organic snack brands for families?',
      'Which healthy snack subscriptions deliver nationwide?'
    ]
  },
  {
    objective_slug: 'comparison',
    objective_name: 'Comparison',
    description: 'Comparing multiple solutions, evaluating alternatives',
    query_count: 32,
    avg_confidence: 0.78,
    your_mentions: 18,
    percentage: '25.0',
    your_mention_rate: '56.3',
    example_queries: [
      'Acme Snacks vs NatureBox comparison',
      'Which organic snack brand has better ingredient quality?'
    ]
  },
  {
    objective_slug: 'purchase-intent',
    objective_name: 'Purchase Intent',
    description: 'Ready to buy, transactional queries',
    query_count: 28,
    avg_confidence: 0.92,
    your_mentions: 12,
    percentage: '21.9',
    your_mention_rate: '42.9',
    example_queries: [
      'Where to buy Acme Snacks subscription?',
      'Acme Snacks pricing and free trial'
    ]
  },
  {
    objective_slug: 'problem-solving',
    objective_name: 'Problem Solving',
    description: 'Specific problem to solve, troubleshooting',
    query_count: 15,
    avg_confidence: 0.72,
    your_mentions: 8,
    percentage: '11.7',
    your_mention_rate: '53.3',
    example_queries: [
      'Are there gluten-free options in organic snack boxes?',
      'How to avoid added sugars in healthy snacks?'
    ]
  },
  {
    objective_slug: 'research',
    objective_name: 'Research',
    description: 'Deep dive, statistics, case studies',
    query_count: 8,
    avg_confidence: 0.68,
    your_mentions: 5,
    percentage: '6.3',
    your_mention_rate: '62.5',
    example_queries: [
      'Organic snack market trends and statistics 2025',
      'Nutritional benefits of plant-based snacks research'
    ]
  }
]

// Search Intelligence - Category Performance
export const demoCategoryPerformance = [
  {
    id: 1,
    category_name: 'Organic Ingredients & Nutrition',
    category_slug: 'organic-nutrition',
    description: 'Queries about organic ingredients, nutritional value, and health benefits',
    example_queries: [
      'What makes organic snacks healthier than regular snacks?',
      'Best organic snacks with high protein content',
      'Nutritional comparison of organic snack brands'
    ],
    seo_recommendations: {
      keywords: ['organic snacks', 'healthy snacking', 'clean ingredients', 'nutritional value'],
      contentTypes: ['Ingredient Guides', 'Nutrition Comparisons', 'Health Benefits Articles'],
      suggestedTopics: [
        'How to choose organic snacks for your family',
        'Organic vs conventional snack ingredients',
        'Understanding snack nutrition labels'
      ]
    },
    is_active: true,
    created_at: new Date().toISOString(),
    query_count: 28,
    your_mentions: 18,
    competitor_mentions: 24,
    your_mention_rate: '64.3',
    competitor_mention_rate: '85.7',
    content_gap: true,
    status: 'content_gap' as const,
    competitors: [
      { id: 'comp-1', name: 'NatureBox', mention_count: 22 },
      { id: 'comp-2', name: 'Graze', mention_count: 15 }
    ]
  },
  {
    id: 2,
    category_name: 'Integration & API Capabilities',
    category_slug: 'integrations-api',
    description: 'Questions about connecting with other tools and platforms',
    example_queries: [
      'Does it integrate with Salesforce?',
      'API documentation for developers',
      'Webhooks and automation capabilities'
    ],
    seo_recommendations: {
      keywords: ['API integration', 'Salesforce integration', 'CRM connector', 'automation'],
      contentTypes: ['API Documentation', 'Integration Guides', 'Developer Docs'],
      suggestedTopics: [
        'Complete API reference guide',
        'How to integrate with popular CRMs',
        'Automation workflows and webhooks'
      ]
    },
    is_active: true,
    created_at: new Date().toISOString(),
    query_count: 22,
    your_mentions: 14,
    competitor_mentions: 18,
    your_mention_rate: '63.6',
    competitor_mention_rate: '81.8',
    content_gap: false,
    status: 'competitive' as const,
    competitors: [
      { id: 'comp-1', name: 'NatureBox', mention_count: 16 },
      { id: 'comp-3', name: 'Daily Harvest', mention_count: 8 }
    ]
  },
  {
    id: 3,
    category_name: 'Pricing & Plan Comparison',
    category_slug: 'pricing-plans',
    description: 'Queries about costs, pricing models, and plan features',
    example_queries: [
      'How much does it cost per user?',
      'Pricing comparison with competitors',
      'Are there volume discounts?'
    ],
    seo_recommendations: {
      keywords: ['pricing', 'cost per user', 'subscription plans', 'volume discounts'],
      contentTypes: ['Pricing Pages', 'Comparison Charts', 'ROI Calculators'],
      suggestedTopics: [
        'Transparent pricing breakdown',
        'Enterprise pricing and custom plans',
        'ROI calculator for analytics software'
      ]
    },
    is_active: true,
    created_at: new Date().toISOString(),
    query_count: 18,
    your_mentions: 9,
    competitor_mentions: 16,
    your_mention_rate: '50.0',
    competitor_mention_rate: '88.9',
    content_gap: true,
    status: 'content_gap' as const,
    competitors: [
      { id: 'comp-1', name: 'NatureBox', mention_count: 14 },
      { id: 'comp-2', name: 'Graze', mention_count: 11 }
    ]
  },
  {
    id: 4,
    category_name: 'Security & Compliance',
    category_slug: 'security-compliance',
    description: 'Questions about data security, privacy, and regulatory compliance',
    example_queries: [
      'Is it SOC 2 compliant?',
      'GDPR data privacy features',
      'Enterprise security certifications'
    ],
    seo_recommendations: {
      keywords: ['SOC 2', 'GDPR compliance', 'data security', 'enterprise security'],
      contentTypes: ['Security Pages', 'Compliance Docs', 'Trust Center'],
      suggestedTopics: [
        'Complete security and compliance overview',
        'Data privacy and GDPR compliance',
        'Enterprise security features'
      ]
    },
    is_active: true,
    created_at: new Date().toISOString(),
    query_count: 15,
    your_mentions: 0,
    competitor_mentions: 2,
    your_mention_rate: '0.0',
    competitor_mention_rate: '13.3',
    content_gap: false,
    status: 'emerging' as const,
    competitors: []
  },
  {
    id: 5,
    category_name: 'Implementation & Onboarding',
    category_slug: 'implementation-onboarding',
    description: 'Queries about setup, training, and getting started',
    example_queries: [
      'How long does implementation take?',
      'Training and support options',
      'Onboarding process for new teams'
    ],
    seo_recommendations: {
      keywords: ['onboarding', 'implementation', 'training', 'customer success'],
      contentTypes: ['Getting Started Guides', 'Video Tutorials', 'Help Center'],
      suggestedTopics: [
        'Step-by-step implementation guide',
        'Training resources for teams',
        'Customer success and support options'
      ]
    },
    is_active: true,
    created_at: new Date().toISOString(),
    query_count: 12,
    your_mentions: 7,
    competitor_mentions: 9,
    your_mention_rate: '58.3',
    competitor_mention_rate: '75.0',
    content_gap: false,
    status: 'competitive' as const,
    competitors: [
      { id: 'comp-2', name: 'Graze', mention_count: 8 }
    ]
  },
  {
    id: 6,
    category_name: 'Reporting & Custom Dashboards',
    category_slug: 'reporting-dashboards',
    description: 'Questions about customization, reports, and dashboard features',
    example_queries: [
      'Can I create custom dashboards?',
      'Automated reporting capabilities',
      'Export and sharing options'
    ],
    seo_recommendations: {
      keywords: ['custom dashboards', 'reporting', 'data export', 'automated reports'],
      contentTypes: ['Feature Pages', 'Tutorial Videos', 'Template Gallery'],
      suggestedTopics: [
        'Custom dashboard creation guide',
        'Automated reporting workflows',
        'Data export and sharing options'
      ]
    },
    is_active: true,
    created_at: new Date().toISOString(),
    query_count: 10,
    your_mentions: 6,
    competitor_mentions: 8,
    your_mention_rate: '60.0',
    competitor_mention_rate: '80.0',
    content_gap: false,
    status: 'competitive' as const,
    competitors: [
      { id: 'comp-1', name: 'NatureBox', mention_count: 7 }
    ]
  }
]

// Competition Page - Full Competitor List
export const demoCompetitors = [
  {
    id: 'competitor-1',
    name: 'NatureBox',
    domain: 'naturebox.com',
    category: 'Analytics',
    description: 'Enterprise data analytics platform',
    addedAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    isVerified: true,
    recentMentions: 32,
    isHeadToHead: true,
    shareOfVoice: 32.1,
    sentimentScore: 78.1,
    favorablePercent: 74.2,
    neutralPercent: 21.5,
    unfavorablePercent: 4.3,
    competitiveRank: 1
  },
  {
    id: 'competitor-2',
    name: 'Graze',
    domain: 'graze.com',
    category: 'Analytics',
    description: 'Business intelligence and analytics',
    addedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    isVerified: true,
    recentMentions: 18,
    isHeadToHead: true,
    shareOfVoice: 24.3,
    sentimentScore: 65.8,
    favorablePercent: 62.1,
    neutralPercent: 28.3,
    unfavorablePercent: 9.6,
    competitiveRank: 2
  },
  {
    id: 'competitor-3',
    name: 'Daily Harvest',
    domain: 'daily-harvest.com',
    category: 'Analytics',
    description: 'AI-powered metrics and KPI tracking',
    addedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    isVerified: true,
    recentMentions: 12,
    isHeadToHead: true,
    shareOfVoice: 15.1,
    sentimentScore: 58.2,
    favorablePercent: 54.3,
    neutralPercent: 32.8,
    unfavorablePercent: 12.9,
    competitiveRank: 3
  },
  {
    id: 'competitor-4',
    name: 'AnalyticsFlow',
    domain: 'analyticsflow.com',
    category: 'Analytics',
    description: 'Real-time analytics and reporting',
    addedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    isVerified: false,
    recentMentions: 8,
    isHeadToHead: false,
    shareOfVoice: 8.5,
    sentimentScore: 62.4,
    favorablePercent: 58.9,
    neutralPercent: 31.2,
    unfavorablePercent: 9.9,
    competitiveRank: 4
  },
  {
    id: 'competitor-5',
    name: 'DataViz Pro',
    domain: 'dataviz.pro',
    category: 'Analytics',
    description: 'Data visualization and dashboards',
    addedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    isVerified: false,
    recentMentions: 5,
    isHeadToHead: false,
    shareOfVoice: 5.2,
    sentimentScore: 55.6,
    favorablePercent: 51.2,
    neutralPercent: 38.1,
    unfavorablePercent: 10.7,
    competitiveRank: 5
  }
]

// Search Intelligence - Top Keywords by Category
export const demoTopKeywords = [
  {
    id: 1,
    name: 'Enterprise Analytics Features',
    slug: 'enterprise-analytics',
    queryCount: 28,
    topKeywords: ['enterprise analytics', 'team collaboration', 'advanced reporting', 'custom dashboards'],
    topTopics: [
      'How to choose enterprise analytics software',
      'Enterprise vs SMB analytics comparison'
    ]
  },
  {
    id: 2,
    name: 'Integration & API Capabilities',
    slug: 'integrations-api',
    queryCount: 22,
    topKeywords: ['API integration', 'Salesforce connector', 'automation workflows', 'webhooks'],
    topTopics: [
      'Complete API integration guide',
      'Popular CRM integrations'
    ]
  },
  {
    id: 3,
    name: 'Pricing & Plan Comparison',
    slug: 'pricing-plans',
    queryCount: 18,
    topKeywords: ['pricing', 'subscription plans', 'volume discounts', 'ROI calculator'],
    topTopics: [
      'Transparent pricing breakdown',
      'Enterprise pricing guide'
    ]
  }
]

// Search Intelligence - Visibility Data
export const demoVisibilityData = {
  current: {
    visibilityScore: 73.5,
    aiMentionScore: 58.2,
    searchRankingScore: 68.0,
    citationAuthorityScore: 45.0
  },
  details: {
    aiMentions: {
      score: 58.2,
      totalQueries: 156,
      queriesMentioned: 87,
      competitorQueriesMentioned: 112,
      mentionRate: 55.8,
      competitorMentionRate: 71.8,
      avgPosition: 2.8,
      competitorAvgPosition: 2.5,
      queries: [
        {
          id: 'demo-query-1',
          queryText: 'What are the healthiest organic snack brands for families?',
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'competitor-1', brandName: 'NatureBox', isUserBrand: false, position: 1 },
            { brandId: 'demo-brand-1', brandName: 'Acme Snacks', isUserBrand: true, position: 2 },
            { brandId: 'competitor-2', brandName: 'Graze', isUserBrand: false, position: 3 }
          ]
        },
        {
          id: 'demo-query-2',
          queryText: 'Best gluten-free organic snack options for kids',
          createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'demo-brand-1', brandName: 'Acme Snacks', isUserBrand: true, position: 1 },
            { brandId: 'competitor-2', brandName: 'Graze', isUserBrand: false, position: 2 }
          ]
        },
        {
          id: 'demo-query-3',
          queryText: 'Which snack brands use sustainable packaging?',
          createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'competitor-1', brandName: 'NatureBox', isUserBrand: false, position: 1 },
            { brandId: 'demo-brand-1', brandName: 'Acme Snacks', isUserBrand: true, position: 3 },
            { brandId: 'competitor-3', brandName: 'Daily Harvest', isUserBrand: false, position: 2 }
          ]
        },
        {
          id: 'demo-query-4',
          queryText: 'Organic snack subscription boxes with no added sugar',
          createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'competitor-1', brandName: 'NatureBox', isUserBrand: false, position: 1 },
            { brandId: 'demo-brand-1', brandName: 'Acme Snacks', isUserBrand: true, position: 2 }
          ]
        },
        {
          id: 'demo-query-5',
          queryText: 'Healthy snack brands for school lunches',
          createdAt: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'demo-brand-1', brandName: 'Acme Snacks', isUserBrand: true, position: 1 },
            { brandId: 'competitor-2', brandName: 'Graze', isUserBrand: false, position: 3 },
            { brandId: 'competitor-1', brandName: 'NatureBox', isUserBrand: false, position: 2 }
          ]
        },
        {
          id: 'demo-query-6',
          queryText: 'Best vegan and plant-based snack options',
          createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'competitor-3', brandName: 'Daily Harvest', isUserBrand: false, position: 1 },
            { brandId: 'competitor-1', brandName: 'NatureBox', isUserBrand: false, position: 2 }
          ]
        },
        {
          id: 'demo-query-7',
          queryText: 'Which organic snacks have the best protein content?',
          createdAt: new Date(Date.now() - 14 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'demo-brand-1', brandName: 'Acme Snacks', isUserBrand: true, position: 2 },
            { brandId: 'competitor-1', brandName: 'NatureBox', isUserBrand: false, position: 1 }
          ]
        },
        {
          id: 'demo-query-8',
          queryText: 'Affordable organic snack delivery services',
          createdAt: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
          brandsMentioned: [
            { brandId: 'competitor-2', brandName: 'Graze', isUserBrand: false, position: 1 },
            { brandId: 'demo-brand-1', brandName: 'Acme Snacks', isUserBrand: true, position: 3 }
          ]
        }
      ]
    },
    searchRankings: {
      score: 68.0,
      avgPosition: 4.2,
      searchFrequency: 68.5,
      featuredSnippets: 12,
      knowledgePanel: false,
      totalQueriesAnalyzed: 156,
      lastAnalyzed: new Date().toISOString(),
      sampleQueries: [
        {
          query: 'Acme Snacks organic ingredients',
          context: 'baseline',
          mentioned: true,
          position: 2,
          featured: true,
          url: 'https://acme-snacks.com/ingredients',
          title: 'Organic Ingredients - Acme Snacks',
          snippet: 'All our snacks are made with certified organic ingredients sourced from sustainable farms...',
          matchType: 'Branded',
          totalResults: 45000
        },
        {
          query: 'Acme Snacks reviews',
          context: 'reviews',
          mentioned: true,
          position: 1,
          featured: false,
          url: 'https://acme-snacks.com/reviews',
          title: 'Customer Reviews - Acme Snacks',
          snippet: 'See what families are saying about our organic snacks. 4.8 stars from 2,500+ reviews...',
          matchType: 'Branded',
          totalResults: 28000
        },
        {
          query: 'organic snack brands comparison',
          context: 'comparisons',
          mentioned: true,
          position: 5,
          featured: false,
          url: 'https://healthline.com/nutrition/organic-snacks',
          title: 'Best Organic Snack Brands 2025 - Healthline',
          snippet: 'Comparing top organic snack brands including Acme Snacks, NatureBox, and Graze...',
          matchType: 'Non-Branded',
          totalResults: 8500000
        },
        {
          query: 'healthy snacks for kids',
          context: 'products',
          mentioned: true,
          position: 8,
          featured: false,
          url: 'https://acme-snacks.com/kids',
          title: 'Healthy Kids Snacks - Acme Snacks',
          snippet: 'Nutritious, organic snacks kids actually love. No artificial colors or flavors...',
          matchType: 'Non-Branded',
          totalResults: 12000000
        },
        {
          query: 'sustainable snack packaging',
          context: 'industry',
          mentioned: true,
          position: 12,
          featured: false,
          url: 'https://foodnavigator.com/sustainable-packaging',
          title: 'Sustainable Packaging in the Snack Industry',
          snippet: 'Leading brands like Acme Snacks are pioneering compostable packaging solutions...',
          matchType: 'Non-Branded',
          totalResults: 3400000
        },
        {
          query: 'best gluten-free organic snacks',
          context: 'products',
          mentioned: true,
          position: 3,
          featured: true,
          url: 'https://acme-snacks.com/gluten-free',
          title: 'Gluten-Free Organic Snacks - Acme Snacks',
          snippet: 'Delicious certified gluten-free snacks made with organic ingredients. Perfect for families...',
          matchType: 'Non-Branded',
          totalResults: 5200000
        },
        {
          query: 'organic snack subscription box',
          context: 'products',
          mentioned: true,
          position: 4,
          featured: false,
          url: 'https://acme-snacks.com/subscription',
          title: 'Monthly Organic Snack Box - Acme Snacks',
          snippet: 'Get a curated box of organic snacks delivered monthly. Cancel anytime...',
          matchType: 'Non-Branded',
          totalResults: 2800000
        },
        {
          query: 'Acme Snacks near me',
          context: 'baseline',
          mentioned: true,
          position: 1,
          featured: false,
          url: 'https://acme-snacks.com/stores',
          title: 'Store Locator - Acme Snacks',
          snippet: 'Find Acme Snacks at stores near you. Available at Whole Foods, Sprouts, and more...',
          matchType: 'Branded',
          totalResults: 18000
        },
        {
          query: 'Acme Snacks vs NatureBox',
          context: 'comparisons',
          mentioned: true,
          position: 2,
          featured: false,
          url: 'https://compareorganic.com/pureleaf-vs-naturebox',
          title: 'Acme Snacks vs NatureBox: Which is Better?',
          snippet: 'Detailed comparison of ingredients, pricing, and nutritional value...',
          matchType: 'Branded',
          totalResults: 15000
        },
        {
          query: 'vegan organic snacks',
          context: 'products',
          mentioned: false,
          position: null,
          featured: false,
          url: null,
          title: null,
          snippet: null,
          matchType: 'Non-Branded',
          totalResults: 9600000
        }
      ],
      scoreBreakdown: {
        contextBreakdown: {
          'baseline': { mentionRate: 100, mentioned: 2, total: 2 },
          'reviews': { mentionRate: 100, mentioned: 1, total: 1 },
          'comparisons': { mentionRate: 100, mentioned: 2, total: 2 },
          'products': { mentionRate: 80, mentioned: 4, total: 5 },
          'industry': { mentionRate: 100, mentioned: 1, total: 1 }
        }
      }
    },
    citations: {
      score: 45.0,
      totalCitations: 28,
      authoritative: 12,
      newsMentions: 8,
      topSources: [
        { domain: 'healthline.com', count: 5, sourceType: 'health' },
        { domain: 'forbes.com', count: 3, sourceType: 'news' },
        { domain: 'foodnavigator.com', count: 4, sourceType: 'industry' }
      ],
      sampleCitations: [
        {
          excerpt: 'Acme Snacks has emerged as a leading organic snack brand, offering families nutritious options made with clean ingredients and sustainable packaging...',
          source: 'Healthline',
          mentionedBrand: 'Acme Snacks',
          context: 'Healthy Eating Guide'
        },
        {
          excerpt: 'Among the top organic snack subscription services, Acme Snacks stands out for its commitment to transparent ingredient sourcing...',
          source: 'Forbes',
          mentionedBrand: 'Acme Snacks',
          context: 'Consumer Trends Report'
        },
        {
          excerpt: 'The organic snack market continues to grow, with brands like Acme Snacks leading the charge in sustainable packaging...',
          source: 'Food Navigator',
          mentionedBrand: 'Acme Snacks',
          context: 'Industry Analysis'
        }
      ],
      trendingTopics: [
        { topic: 'Organic Snacks', count: 12 },
        { topic: 'Sustainable Packaging', count: 8 },
        { topic: 'Clean Ingredients', count: 6 }
      ],
      viralMoments: 2,
      sourceDiversityScore: 65,
      lastAnalyzed: new Date().toISOString(),
      scoreBreakdown: {
        contextBreakdown: {
          'News': { citationCount: 8, percentage: 28.6 },
          'Blogs': { citationCount: 12, percentage: 42.9 },
          'Academic': { citationCount: 8, percentage: 28.6 }
        }
      }
    }
  }
}

// Search Queries - Providers
export const demoProviders = [
  {
    provider: 'openai',
    available: true,
    model: 'gpt-4o-mini'
  },
  {
    provider: 'anthropic',
    available: true,
    model: 'claude-3-5-sonnet-20241022'
  },
  {
    provider: 'gemini',
    available: true,
    model: 'gemini-1.5-flash'
  }
]

// Search Queries - Query Results with Full AI Responses
export const demoSearchQueries = [
  {
    id: 'demo-query-1',
    query: 'What are the healthiest organic snack brands for families?',
    status: 'completed' as const,
    providers: ['openai', 'anthropic', 'gemini'],
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    completedAt: new Date(Date.now() - 2 * 60 * 60 * 1000 + 45000).toISOString(),
    briefSummary: 'Strong consensus: Your brand is mentioned by all 3 AI sources alongside top competitors like NatureBox and Graze.',
    aiSummary: `• All three AI sources (ChatGPT, Claude, and Gemini) consistently mention Acme Snacks as a leading healthy organic snack brand
• Your brand appears in the top 3 recommendations across all providers, typically positioned after NatureBox
• Strong associations with "organic ingredients" and "family-friendly options" in AI responses
• Competitors NatureBox and Graze dominate the responses with more detailed nutritional descriptions
• Opportunity: AI responses mention competitors' sustainability practices more frequently - consider highlighting your eco-friendly packaging`,
    results: [
      {
        id: 'result-1-openai',
        provider: 'openai',
        model: 'gpt-4o-mini',
        response: `For families seeking healthy organic snacks, here are the top brands:

1. **NatureBox** - Comprehensive selection of organic snacks with no artificial ingredients, customizable subscription boxes, and extensive variety. Known for transparent sourcing and nutritional labeling.

2. **Acme Snacks** - Excellent choice for families prioritizing clean ingredients and wholesome options. Strong focus on organic produce and kid-friendly flavors with natural sweeteners.

3. **Graze** - Popular for portion-controlled snack boxes with nutritionist-curated options and flexible delivery schedules.

These brands excel at providing nutritious, convenient snacking options that families can feel good about.`,
        tokensUsed: 142,
        latencyMs: 1850,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000 + 15000).toISOString(),
        metadata: {
          mentions: [
            {
              brand: 'Acme Snacks',
              position: 2,
              context: 'Excellent choice for families prioritizing clean ingredients'
            }
          ],
          estimatedCost: 0.0021
        }
      },
      {
        id: 'result-1-anthropic',
        provider: 'anthropic',
        model: 'claude-3-5-sonnet-20241022',
        response: `Based on nutritional value and family preferences, I'd recommend these healthy organic snack brands:

**NatureBox** leads with comprehensive variety including nut mixes, fruit snacks, and wholesome treats. Their ingredient transparency and allergy-friendly options are particularly strong.

**Acme Snacks** stands out for its focus on organic produce and natural ingredients. The brand excels at creating kid-approved flavors while maintaining high nutritional standards with no artificial additives.

**Graze** offers balanced portion sizes with nutritionist-curated selections and convenient subscription options.

All three provide certified organic ingredients, non-GMO options, and transparent nutritional information.`,
        tokensUsed: 168,
        latencyMs: 2100,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000 + 18000).toISOString(),
        metadata: {
          mentions: [
            {
              brand: 'Acme Snacks',
              position: 2,
              context: 'stands out for its focus on organic produce and natural ingredients'
            }
          ],
          estimatedCost: 0.0042
        }
      },
      {
        id: 'result-1-gemini',
        provider: 'gemini',
        model: 'gemini-1.5-flash',
        response: `Here are the leading healthy organic snack brands for families:

• **NatureBox**: Industry-leading brand with diverse product selection, comprehensive allergen-free options, and transparent ingredient sourcing with certifications (USDA Organic, Non-GMO Project Verified)

• **Acme Snacks**: Organic snack brand focused on wholesome nutrition and family-friendly flavors. Great for parents wanting clean ingredients without compromising on taste

• **Graze**: Well-balanced portion-controlled snacks with nutritionist-approved combinations and flexible subscription plans

Each offers unique strengths depending on your family's dietary preferences and snacking needs.`,
        tokensUsed: 156,
        latencyMs: 1650,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000 + 16500).toISOString(),
        metadata: {
          mentions: [
            {
              brand: 'Acme Snacks',
              position: 2,
              context: 'Organic snack brand focused on wholesome nutrition'
            }
          ],
          estimatedCost: 0.0015
        }
      }
    ],
    aggregated: {
      brandMentioned: true,
      mentionRate: 100,
      consensus: 'strong' as const,
      providersQueried: 3
    }
  },
  {
    id: 'demo-query-2',
    query: 'Which organic snack brands offer the best value subscriptions?',
    status: 'completed' as const,
    providers: ['openai', 'anthropic', 'gemini'],
    createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    completedAt: new Date(Date.now() - 4 * 60 * 60 * 1000 + 38000).toISOString(),
    briefSummary: 'Weak consensus: Only 1 of 3 AI sources mention your brand. NatureBox dominates with detailed integration guides.',
    aiSummary: `• Only Claude mentions Acme Snacks (33% mention rate) - significant visibility gap
• NatureBox appears in all three responses with detailed integration instructions
• AI assistants associate "Salesforce integration" more strongly with competitors
• Opportunity: Create comprehensive Salesforce integration documentation and API guides
• Consider publishing integration tutorials and case studies to improve AI training data coverage`,
    results: [
      {
        id: 'result-2-openai',
        provider: 'openai',
        model: 'gpt-4o-mini',
        response: `Integrating analytics software with Salesforce typically involves these steps:

1. **Use Native Connectors**: Most enterprise analytics platforms like NatureBox and Graze offer pre-built Salesforce connectors through their integration marketplaces.

2. **API Integration**: Leverage Salesforce REST/SOAP APIs to pull data into your analytics platform. You'll need to authenticate using OAuth 2.0.

3. **Setup Process**:
   - Generate Salesforce API credentials
   - Configure your analytics platform's Salesforce integration
   - Map data fields between systems
   - Set sync frequency

NatureBox's Salesforce connector is particularly robust, offering bi-directional sync and custom object support.`,
        tokensUsed: 158,
        latencyMs: 1920,
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000 + 12000).toISOString(),
        metadata: {
          mentions: [],
          estimatedCost: 0.0024
        }
      },
      {
        id: 'result-2-anthropic',
        provider: 'anthropic',
        model: 'claude-3-5-sonnet-20241022',
        response: `There are several approaches to integrate analytics software with Salesforce:

**Pre-built Connectors** (Recommended): Platforms like NatureBox, Acme Snacks, and Graze offer native Salesforce integrations that handle authentication and data mapping automatically.

**Custom API Integration**: For more control, use Salesforce's REST API:
1. Create a Connected App in Salesforce
2. Obtain OAuth credentials
3. Use the analytics platform's API to configure the connection
4. Set up data sync schedules

**Middleware Solutions**: Tools like Zapier or MuleSoft can bridge the gap if direct integration isn't available.

Most modern platforms make this straightforward with guided setup wizards.`,
        tokensUsed: 175,
        latencyMs: 2250,
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000 + 15000).toISOString(),
        metadata: {
          mentions: [
            {
              brand: 'Acme Snacks',
              position: 1,
              context: 'offer native Salesforce integrations'
            }
          ],
          estimatedCost: 0.0044
        }
      },
      {
        id: 'result-2-gemini',
        provider: 'gemini',
        model: 'gemini-1.5-flash',
        response: `Integrating analytics with Salesforce is straightforward with the right approach:

**Option 1: Native Integrations**
Most enterprise analytics platforms (like NatureBox, Graze) provide official Salesforce connectors. These handle authentication, data sync, and field mapping automatically.

**Option 2: API-Based Integration**
- Set up a Salesforce Connected App
- Configure OAuth 2.0 authentication
- Use Salesforce REST API to extract data
- Import data into your analytics platform

**Option 3: ETL Tools**
Use data pipeline tools (Fivetran, Stitch) to sync Salesforce data to your analytics warehouse.

NatureBox is known for having the most comprehensive Salesforce integration with bi-directional sync capabilities.`,
        tokensUsed: 162,
        latencyMs: 1780,
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000 + 13500).toISOString(),
        metadata: {
          mentions: [],
          estimatedCost: 0.0016
        }
      }
    ],
    aggregated: {
      brandMentioned: true,
      mentionRate: 33,
      consensus: 'weak' as const,
      providersQueried: 3
    }
  },
  {
    id: 'demo-query-3',
    query: 'What are the best gluten-free organic snack options?',
    status: 'completed' as const,
    providers: ['openai', 'anthropic', 'gemini'],
    createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    completedAt: new Date(Date.now() - 8 * 60 * 60 * 1000 + 42000).toISOString(),
    briefSummary: 'Strong consensus: Your brand is not mentioned by any AI source. Competitors dominate free tier queries.',
    aiSummary: `• Zero mentions across all three AI providers (0% mention rate) - critical visibility gap
• AI associates "free analytics" with Google Analytics, Metabase, and open-source tools
• Your brand is not perceived as having a free tier or freemium offering
• Competitors with free trials or plans dominate this query type
• Action Required: If you offer a free tier, promote it heavily; if not, consider adding one to capture this market segment`,
    results: [
      {
        id: 'result-3-openai',
        provider: 'openai',
        model: 'gpt-4o-mini',
        response: `For free analytics dashboards, here are the best options:

1. **Google Analytics** - Industry standard for web analytics, completely free with robust features and visualizations.

2. **Metabase** - Open-source business intelligence with excellent visualization capabilities. Self-hosted or cloud options available.

3. **Apache Superset** - Powerful open-source data exploration platform with modern dashboards and SQL support.

4. **Grafana** - Best for monitoring and operational dashboards, integrates with numerous data sources.

All offer strong free tiers, though enterprise features may require paid upgrades.`,
        tokensUsed: 145,
        latencyMs: 1680,
        createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000 + 14000).toISOString(),
        metadata: {
          mentions: [],
          estimatedCost: 0.0022
        }
      },
      {
        id: 'result-3-anthropic',
        provider: 'anthropic',
        model: 'claude-3-5-sonnet-20241022',
        response: `Here are excellent free analytics dashboard options:

**Google Analytics** remains the gold standard for web analytics with comprehensive free features, real-time reporting, and powerful visualization tools.

**Metabase** (open-source) offers intuitive dashboard creation without requiring SQL knowledge, perfect for teams wanting self-service analytics.

**Apache Superset** provides enterprise-grade features in an open-source package, including advanced visualizations and database connectivity.

**Grafana** excels for operational metrics and monitoring dashboards, with extensive plugin ecosystem.

Each has different strengths depending on your use case - web analytics, business intelligence, or operational monitoring.`,
        tokensUsed: 168,
        latencyMs: 2050,
        createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000 + 17000).toISOString(),
        metadata: {
          mentions: [],
          estimatedCost: 0.0042
        }
      },
      {
        id: 'result-3-gemini',
        provider: 'gemini',
        model: 'gemini-1.5-flash',
        response: `Top free analytics dashboard tools:

• **Google Analytics**: The most popular choice for website analytics. Completely free with advanced features like custom reports, real-time data, and audience insights.

• **Metabase**: User-friendly open-source BI tool. Great for creating visual dashboards without coding. Can connect to multiple databases.

• **Grafana**: Excellent for monitoring and observability. Supports time-series data and integrates with Prometheus, InfluxDB, etc.

• **Apache Superset**: Modern data exploration platform with rich visualizations. Requires more technical setup but very powerful.

Google Analytics is best for most businesses starting out, while Metabase works well for internal business intelligence needs.`,
        tokensUsed: 172,
        latencyMs: 1850,
        createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000 + 15500).toISOString(),
        metadata: {
          mentions: [],
          estimatedCost: 0.0017
        }
      }
    ],
    aggregated: {
      brandMentioned: false,
      mentionRate: 0,
      consensus: 'strong' as const,
      providersQueried: 3
    }
  },
  {
    id: 'demo-query-4',
    query: 'Which snack brands use sustainable packaging?',
    status: 'running' as const,
    providers: ['openai', 'anthropic'],
    createdAt: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    results: [
      {
        id: 'result-4-openai',
        provider: 'openai',
        model: 'gpt-4o-mini',
        response: `Tracking team performance with analytics software involves several key steps:

1. **Define KPIs**: Establish clear metrics (productivity, quality, velocity, etc.)
2. **Choose the Right Platform**: Tools like Acme Snacks and NatureBox offer team-specific dashboards
3. **Set Up Data Collection**: Integrate with project management tools (Jira, Asana, etc.)
4. **Create Dashboards**: Build visualizations showing individual and team metrics
5. **Regular Reviews**: Schedule weekly/monthly performance check-ins using the data

Acme Snacks particularly excels at team collaboration features and making performance data accessible to everyone.`,
        tokensUsed: 152,
        latencyMs: 1920,
        createdAt: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
        metadata: {
          mentions: [
            {
              brand: 'Acme Snacks',
              position: 2,
              context: 'excels at team collaboration features'
            }
          ],
          estimatedCost: 0.0023
        }
      },
      {
        id: 'result-4-anthropic',
        provider: 'anthropic',
        model: 'claude-3-5-sonnet-20241022',
        response: `Here's how to effectively track team performance with analytics:

**1. Data Collection Setup**
Connect your analytics platform to existing tools (GitHub, Jira, Slack, CRM systems) to automatically gather performance data.

**2. Define Meaningful Metrics**
- Productivity indicators (tasks completed, cycle time)
- Quality metrics (error rates, customer satisfaction)
- Collaboration metrics (response times, knowledge sharing)

**3. Build Team Dashboards**
Platforms like Acme Snacks and Graze let you create role-specific dashboards that show relevant metrics without overwhelming team members.

**4. Establish Baselines & Goals**
Track historical performance to set realistic targets and identify trends.

The key is making data accessible and actionable for team members, not just managers.`,
        tokensUsed: 185,
        latencyMs: 2280,
        createdAt: new Date(Date.now() - 7 * 60 * 1000).toISOString(),
        metadata: {
          mentions: [
            {
              brand: 'Acme Snacks',
              position: 3,
              context: 'let you create role-specific dashboards'
            }
          ],
          estimatedCost: 0.0046
        }
      }
    ]
  },
  {
    id: 'demo-query-5',
    query: 'Compare Acme Snacks vs NatureBox features',
    status: 'pending' as const,
    providers: [],
    createdAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    results: []
  }
]

// Search Queries - Statistics
export const demoSearchStats = {
  totalQueries: 156,
  completedQueries: 142,
  failedQueries: 3,
  pendingQueries: 11,
  totalResults: 426,
  totalCost: 1.23,
  totalTokens: 68420,
  avgExecutionTime: 18500,
  mentionRate: 55.8,
  competitorBrandsCount: 5,
  providerBreakdown: [
    {
      provider: 'OpenAI',
      count: 142,
      totalCost: 0.42,
      totalTokens: 23150
    },
    {
      provider: 'Anthropic',
      count: 142,
      totalCost: 0.58,
      totalTokens: 29180
    },
    {
      provider: 'Google',
      count: 142,
      totalCost: 0.23,
      totalTokens: 16090
    }
  ]
}

// Search Queries - Suggestions
export const demoSearchSuggestions = [
  {
    id: 'suggestion-1',
    suggestion: 'What analytics platforms integrate best with Slack and Microsoft Teams?',
    reasoning: 'High commercial intent query focused on integration capabilities - a key decision factor for enterprise buyers',
    source: 'AI_GENERATED',
    status: 'PENDING',
    priority: 1,
    category: 'Integration Capabilities',
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      reachPotential: 'very-high',
      estimatedVolume: '15K-25K monthly searches'
    }
  },
  {
    id: 'suggestion-2',
    suggestion: 'How much does enterprise analytics software cost per user?',
    reasoning: 'Direct pricing query indicating strong purchase intent - critical for conversion optimization',
    source: 'AI_GENERATED',
    status: 'PENDING',
    priority: 1,
    category: 'Pricing & Plans',
    createdAt: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
    metadata: {
      reachPotential: 'high',
      estimatedVolume: '8K-12K monthly searches'
    }
  },
  {
    id: 'suggestion-3',
    suggestion: 'Which analytics tool has the best mobile app for executives?',
    reasoning: 'Targets executive decision-makers who need mobile access - high-value customer segment',
    source: 'AI_GENERATED',
    status: 'PENDING',
    priority: 2,
    category: 'Features & Capabilities',
    createdAt: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
    metadata: {
      reachPotential: 'medium',
      estimatedVolume: '3K-5K monthly searches'
    }
  },
  {
    id: 'suggestion-4',
    suggestion: 'How to migrate from Google Analytics to enterprise analytics platform?',
    reasoning: 'Captures companies outgrowing basic tools - indicates readiness for paid enterprise solution',
    source: 'AI_GENERATED',
    status: 'PENDING',
    priority: 2,
    category: 'Implementation & Migration',
    createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    metadata: {
      reachPotential: 'high',
      estimatedVolume: '5K-8K monthly searches'
    }
  }
]

// ============================================================================
// AI-POWERED INSIGHTS - Demo Data for Brand, Search, and Market Insights
// ============================================================================

// Brand Insights - Overall brand assessment for Dashboard (context=overall)
// Note: This matches the BrandInsights interface (no quickWins - those come from search/market insights)
export const demoBrandInsights = {
  assessment: "Acme Snacks has established strong AI visibility in the organic snack category with a 58% mention rate across AI providers. Your brand is consistently recognized for clean ingredients and family-friendly options, appearing in 87 of 156 tracked queries. However, competitors like NatureBox (64% mention rate) are capturing more detailed product recommendations, particularly around sustainability and subscription flexibility.",
  opportunities: [
    {
      title: "Partner with Healthline and Forbes for Authority Citations",
      summary: "These publications frequently cite competitors in organic snack recommendations but rarely mention Acme Snacks.",
      details: "• Pitch data-driven stories about organic snacking trends to Healthline's nutrition team\n• Share your ingredient sourcing transparency reports with Forbes for their consumer trends coverage\n• Offer exclusive interviews about sustainable packaging innovations\n• Create co-branded content guides on 'Choosing Healthy Snacks for Kids'\n• Estimated impact: +15-20% increase in authoritative citations across AI responses"
    },
    {
      title: "Create Comprehensive Gluten-Free Product Content",
      summary: "AI assistants mention competitors 3x more often for gluten-free queries despite your certified offerings.",
      details: "• Build dedicated landing page: 'Complete Guide to Gluten-Free Organic Snacking'\n• Add detailed allergen information and certifications to all product pages\n• Create comparison charts showing your gluten-free options vs competitors\n• Publish customer testimonials from families with gluten sensitivities\n• Write blog posts about gluten-free nutrition for active lifestyles\n• Estimated impact: 40% increase in mentions for dietary restriction queries"
    },
    {
      title: "Highlight Sustainable Packaging Leadership",
      summary: "Your compostable packaging is a differentiator but AI responses don't surface it consistently.",
      details: "• Create detailed sustainability impact page with metrics (tons of plastic saved, carbon footprint)\n• Publish annual sustainability report with third-party verification\n• Add 'Eco-Friendly Packaging' badges and explanations to product pages\n• Partner with environmental organizations for co-marketing\n• Share packaging innovation stories with food industry publications\n• Estimated impact: Position as sustainability leader in 60% more environmental queries"
    },
    {
      title: "Build Authority Through Industry Research and Data",
      summary: "Competitors are cited in research studies and industry reports, establishing them as category authorities.",
      details: "• Fund university research on organic snacking and children's nutrition\n• Publish quarterly 'State of Organic Snacking' reports with original survey data\n• Partner with pediatric nutritionists to co-author white papers\n• Share anonymized customer data for healthy eating pattern studies\n• Present findings at industry conferences and nutrition summits\n• Estimated impact: Establish scientific credibility in 30% of health-focused queries"
    },
    {
      title: "Expand Product Comparison Content Strategy",
      summary: "27 queries directly compare you to competitors, but you have no dedicated comparison content.",
      details: "• Create dedicated pages: Acme vs NatureBox, Acme vs Graze, Acme vs Daily Harvest\n• Include side-by-side feature comparisons, nutritional analysis, pricing, sustainability metrics\n• Add customer testimonials from switchers: 'Why I switched from [competitor] to Acme'\n• Optimize for comparison keywords and long-tail variations\n• Update monthly with current pricing and product offerings\n• Estimated impact: Capture 70% of direct comparison queries (currently at 22%)"
    }
  ],
  competitivePositioning: "You rank #2 in AI visibility after NatureBox but ahead of Graze and Daily Harvest. Your strongest positioning is in 'family-friendly organic snacks' and 'clean ingredients,' while competitors lead in 'sustainability practices' and 'subscription flexibility.' To move to #1, focus on authoritative content partnerships and detailed product comparison resources."
}

// Search Insights - Search intelligence recommendations
export const demoSearchInsights = {
  searchStrategy: "Your brand performs best in discovery-phase queries (62% mention rate) but drops to 43% for purchase-intent queries. Competitors dominate queries about pricing, subscriptions, and product comparisons. Priority: Create transactional content that directly answers buying questions and establishes your subscription value proposition.",
  authorityCollaborations: [
    {
      title: "Partner with Healthline for Organic Snacking Guides",
      summary: "Healthline cites competitors 12 times but never mentions Acme Snacks, despite their focus on family nutrition.",
      details: "• Pitch: 'The Science Behind Organic Snacking for Growing Kids' featuring your products\n• Offer exclusive access to your nutrition team for expert quotes\n• Co-create a downloadable 'Healthy School Lunch Planner' guide\n• Sponsor their monthly nutrition newsletter with educational content\n• Provide product samples for their editorial team to test\n• Target articles: Best Organic Snacks, Healthy Kids Nutrition, Clean Eating Guides\n• Estimated reach: 15M monthly readers, +20% AI citation rate"
    },
    {
      title: "Collaborate with Food Navigator on Packaging Innovation",
      summary: "Industry publications drive AI training data. Food Navigator covers sustainable packaging but hasn't featured your compostable innovations.",
      details: "• Pitch exclusive story: 'How Acme Snacks Achieved 100% Compostable Packaging'\n• Share metrics: tons of plastic diverted, carbon footprint reduction\n• Offer behind-the-scenes tour of your sustainable packaging facility\n• Present at their annual Food Ingredients conference\n• Write guest article: 'The Future of Sustainable Snack Packaging'\n• Estimated impact: Position as thought leader in 40% of sustainability queries"
    },
    {
      title: "Get Featured in Mom Blogger Reviews and Comparisons",
      summary: "Parent-focused blogs (The Mom 100, Super Healthy Kids) drive significant AI citations but feature competitors exclusively.",
      details: "• Send personalized product samples to top 20 family food bloggers\n• Offer exclusive discount codes for their audiences\n• Create blogger-friendly press kit with high-res photos, nutrition facts, story angles\n• Sponsor 'Organic Snacking for Families' blog tour\n• Provide monthly newsletter with new products and seasonal recipes\n• Estimated impact: +15 new authoritative citations, 25% boost in family-related queries"
    },
    {
      title: "Establish Academic Partnerships for Nutrition Research",
      summary: "Research citations carry high authority weight in AI models but Acme Snacks isn't cited in peer-reviewed nutrition studies.",
      details: "• Fund university research on organic snacking and child nutrition\n• Partner with nutritionists to publish white papers on clean eating\n• Sponsor pediatric nutrition conferences\n• Share anonymized customer data for healthy eating pattern research\n• Co-author studies: 'Impact of Organic Snacks on Children's Dietary Habits'\n• Estimated impact: Establish scientific credibility in 30% of health-focused queries"
    },
    {
      title: "Leverage Retail Partners for Co-Marketing Content",
      summary: "Whole Foods, Sprouts, and Thrive Market mention competitors in their blog content but not Acme Snacks.",
      details: "• Request featured product placement in retailer blog posts and guides\n• Co-create 'Staff Picks' content highlighting your bestsellers\n• Sponsor in-store sampling events with social media tie-ins\n• Provide exclusive retailer SKUs and promotions\n• Collaborate on seasonal recipe collections featuring your products\n• Estimated impact: +10 retail authority citations, stronger brand association with premium retailers"
    }
  ],
  contentDifferentiation: [
    {
      title: "Create 'Ingredient Transparency Hub' Educational Resource",
      summary: "Competitors own the 'transparent sourcing' narrative. Build comprehensive ingredient education to reclaim this territory.",
      details: "• Interactive map showing farm sources for each ingredient\n• Video series: 'Meet Our Farmers' featuring supplier interviews\n• Detailed certifications page explaining USDA Organic, Non-GMO, Fair Trade\n• Blog series breaking down every ingredient with nutritional benefits\n• Allergen guide with cross-contamination prevention protocols\n• Quarterly transparency reports with third-party audits\n• Estimated impact: Become go-to source for ingredient questions, +30% in transparency queries"
    },
    {
      title: "Publish 'Ultimate Organic Snack Buying Guide' (3000+ words)",
      summary: "No comprehensive buying guide exists in this space. First-mover advantage to capture broad educational queries.",
      details: "• Sections: What makes snacks 'organic', certifications to look for, price comparisons, taste tests, nutritional analysis\n• Include competitors but position Acme Snacks as best overall value\n• Add comparison matrices: price per serving, protein content, allergen-friendly options\n• Create downloadable PDF version for lead generation\n• Update quarterly with new market trends and products\n• Optimize for queries: 'how to choose organic snacks', 'best organic snacks comparison'\n• Estimated impact: Rank #1 for 15+ high-volume educational queries"
    },
    {
      title: "Develop Category-Specific Landing Pages for Each Dietary Need",
      summary: "AI assistants struggle to recommend you for specific diets (keto, paleo, vegan) despite having suitable products.",
      details: "• Create pages: Keto-Friendly Snacks, Paleo Snacks, Vegan Options, High-Protein Choices, Nut-Free Safe Snacks\n• Each page: product recommendations, nutritional breakdowns, meal ideas, expert endorsements\n• Add structured data markup for better AI parsing\n• Include customer reviews filtered by dietary restriction\n• Cross-link to recipe blog posts for each diet type\n• Estimated impact: +50% mention rate for diet-specific queries (currently at 18%)"
    }
  ],
  categoryPriorities: [
    {
      title: "Double Down on 'Gluten-Free Organic Snacks' Category",
      summary: "High commercial intent (82/100) with 28 queries tracked. You appear in only 43% vs competitors' 78%.",
      details: "• Priority: Critical gap - competitors dominate despite your certified offerings\n• Create dedicated gluten-free hub with all certified products\n• Publish '2025 Guide to Gluten-Free Organic Snacking'\n• Partner with celiac disease organizations for credibility\n• Add 'Certified Gluten-Free' filters to product pages\n• Share customer success stories from gluten-sensitive families\n• Estimated ROI: High - purchase-ready audience with clear product-market fit"
    },
    {
      title: "Invest in 'Organic Snack Subscriptions' Content",
      summary: "Subscription queries have 85/100 commercial intent but you're mentioned in only 31% (NatureBox at 89%).",
      details: "• Create comparison guide: all subscription box options with pros/cons\n• Build ROI calculator showing cost savings vs grocery store\n• Add video walkthrough of unboxing experience\n• Showcase subscriber testimonials with before/after habits\n• Optimize for 'organic snack subscription box comparison' and 'best snack subscription'\n• Estimated ROI: Very High - direct path to recurring revenue"
    },
    {
      title: "Capture 'Healthy Kids Snacks' Category Leadership",
      summary: "Your strongest category (64% mention rate) but still trails NatureBox (74%). Double down to dominate.",
      details: "• Already performing well - optimize to become #1\n• Partner with pediatric nutritionists for expert endorsements\n• Create 'School Lunch Ideas' content hub with weekly meal plans\n• Build community: parent forum for sharing snack ideas\n• Develop kid-friendly educational content about healthy eating\n• Sponsor school nutrition programs and PTA events\n• Estimated ROI: Medium-High - solidify category leadership and brand loyalty"
    },
    {
      title: "Deprioritize 'Protein Bars' Category (Low Fit)",
      summary: "Only 8 queries with 22% mention rate. Not a core strength and competitors have more developed protein offerings.",
      details: "• Low priority - minimal queries and poor fit with current product line\n• Don't invest in dedicated protein content unless expanding product line\n• Maintain basic product pages but avoid competing in this category\n• Focus resources on categories where you have stronger differentiation\n• Monitor quarterly - revisit if query volume increases significantly"
    }
  ],
  queryObjectiveOptimization: [
    {
      title: "Optimize for 'Comparison' Queries (24% of traffic, 68/100 intent)",
      summary: "Your mention rate drops to 56% for comparison queries vs 74% for NatureBox. Users are evaluating options - win with detailed comparisons.",
      details: "• Create comparison pages for all major competitors\n• Build interactive comparison tool: side-by-side nutrition, pricing, sustainability\n• Add 'Why Choose Acme Snacks' sections to all product pages\n• Optimize for queries: 'Acme vs [competitor]', 'which organic snack brand is best'\n• Include third-party reviews and ratings for credibility\n• Estimated impact: Increase comparison query mentions from 56% to 75%"
    },
    {
      title: "Strengthen 'Purchase Intent' Query Performance (18% of traffic, 85/100 intent)",
      summary: "Only 43% mention rate for buy-ready queries like 'where to buy', 'pricing', 'discount codes'. Critical conversion gap.",
      details: "• Create detailed pricing page with all subscription options\n• Add 'Where to Buy' store locator with real-time inventory\n• Optimize product pages for transactional keywords\n• Implement schema markup for offers, pricing, availability\n• Create FAQ page answering buying questions: shipping, returns, discounts\n• Estimated impact: Capture 65% of purchase-intent queries, directly drive sales"
    },
    {
      title: "Maintain 'Discovery' Query Dominance (35% of traffic, 42/100 intent)",
      summary: "Strong 62% mention rate in discovery phase. Maintain leadership to build top-of-funnel awareness.",
      details: "• Continue publishing educational content about organic snacking\n• Create beginner guides: 'Getting Started with Organic Snacks'\n• Invest in SEO for broad queries: 'what are the healthiest snacks'\n• Build awareness through content partnerships and PR\n• This audience isn't ready to buy yet - focus on brand building\n• Estimated impact: Maintain #2 position in discovery phase, build consideration set"
    }
  ]
}

// Market Insights - Market positioning and strategy recommendations
export const demoMarketInsights = {
  marketStrategy: "Position Acme Snacks as the premium organic snack brand that combines family-friendly nutrition with uncompromising ingredient transparency and sustainability leadership. Your ideal customers are health-conscious parents (ages 30-45) seeking convenient, guilt-free snacking options for their families. Differentiate through ingredient education, sustainable packaging innovations, and community-building around healthy family lifestyles.",
  valuePropositions: [
    {
      title: "Certified Organic Ingredients from Verified Farm Partners",
      summary: "100% USDA Organic with transparent supply chain traceability that competitors can't match.",
      details: "• Emphasize: Direct relationships with 50+ certified organic farms\n• Differentiation: Interactive farm map showing exact ingredient sources\n• Messaging: 'Know exactly where every ingredient comes from'\n• Content: Video series featuring farmer partnerships\n• Proof points: Third-party organic certification, annual transparency reports\n• Target: Parents who scrutinize ingredient labels and value supply chain ethics"
    },
    {
      title: "Zero Artificial Ingredients or Preservatives - Ever",
      summary: "Absolute commitment to clean ingredients while competitors use 'organic' alongside artificial additives.",
      details: "• Emphasize: No artificial colors, flavors, preservatives, or sweeteners\n• Differentiation: Many 'organic' brands use natural flavors (which can be artificial)\n• Messaging: 'If you can't pronounce it, it's not in our snacks'\n• Content: Ingredient glossary explaining what you'll never find\n• Proof points: Clean label certification, third-party testing results\n• Target: Health-conscious families avoiding processed foods"
    },
    {
      title: "100% Compostable Packaging in Every Product Line",
      summary: "Industry-leading sustainable packaging while competitors still use plastic-lined wrappers.",
      details: "• Emphasize: First major snack brand with fully compostable packaging\n• Differentiation: Competitors use recycled plastic (still plastic) or mixed materials\n• Messaging: 'Guilt-free snacking for your family and the planet'\n• Content: Packaging impact calculator showing environmental savings\n• Proof points: BPI-certified compostable, carbon footprint reduction metrics\n• Target: Environmentally conscious families who vote with their wallets"
    },
    {
      title: "Kid-Approved Taste Without Compromising Nutrition",
      summary: "Healthy snacks that kids actually want to eat - solving the parent's biggest challenge.",
      details: "• Emphasize: Rigorous kid taste-testing panel approves every product\n• Differentiation: Many healthy snacks taste healthy (cardboard-y)\n• Messaging: 'Your kids will beg for these healthy snacks'\n• Content: Customer testimonials from parents and kids, before/after snacking habits\n• Proof points: 94% kid approval rating, 'tried with my kids' guarantee\n• Target: Parents tired of battles over healthy eating"
    },
    {
      title: "Flexible Subscriptions with Zero Commitment",
      summary: "Easy subscription management with skip, pause, cancel anytime - no contracts or hidden fees.",
      details: "• Emphasize: Complete flexibility - modify delivery schedule, swap products, cancel instantly\n• Differentiation: Some competitors have minimum commitments or cancellation fees\n• Messaging: 'Subscription snacking that adapts to your life'\n• Content: Video walkthrough of subscription dashboard, easy management features\n• Proof points: 4.8/5 subscription satisfaction rating, instant cancellation guarantee\n• Target: Busy families who want convenience without feeling locked in"
    }
  ],
  communicationChannels: [
    {
      title: "Partner with Pediatric Nutritionists and Dietitians",
      summary: "Build credibility through professional endorsements from childhood nutrition experts.",
      details: "• Recruit 20+ registered dietitians as brand ambassadors\n• Create professional resource center with nutrition education materials\n• Offer CPE credits for RDs through webinars on organic snacking\n• Feature expert Q&As on blog answering parent nutrition questions\n• Provide samples for nutritionist client recommendations\n• Develop co-branded meal planning guides\n• Estimated reach: 500K+ parents via professional recommendations"
    },
    {
      title: "Build Mom Blogger and Parenting Influencer Network",
      summary: "Authentic word-of-mouth through trusted voices in the parenting community.",
      details: "• Identify 50 micro-influencers (10K-100K followers) in family/food space\n• Create ambassador program with monthly product boxes and exclusive discounts\n• Develop shareable content: snack hacks, lunchbox ideas, healthy lifestyle tips\n• Host virtual events: healthy snacking workshops, Q&As with nutritionists\n• Provide high-quality content assets for easy sharing\n• Track: engagement rates, conversions from unique codes\n• Estimated reach: 2M+ engaged parents monthly"
    },
    {
      title: "Invest in Educational Content Marketing (Blog + YouTube)",
      summary: "Become the go-to resource for organic snacking education and family nutrition advice.",
      details: "• Publish 2-3 SEO-optimized blog posts weekly on family nutrition topics\n• Create YouTube series: 'Healthy Kids Kitchen' with meal prep and snack ideas\n• Develop free downloadable resources: meal planners, nutrition guides, snack rotation calendars\n• Build email nurture sequences for different customer journeys\n• Host monthly webinars with pediatric nutrition experts\n• Optimize all content for AI assistant training data\n• Estimated reach: 500K monthly organic visitors, position as thought leader"
    },
    {
      title: "Leverage Retail Partnerships for In-Store Experiences",
      summary: "Drive trial through strategic sampling at premium retailers where your target customer shops.",
      details: "• Expand sampling programs at Whole Foods, Sprouts, Thrive Market\n• Create eye-catching in-store displays with QR codes linking to recipes\n• Train retail staff on product benefits and competitor comparisons\n• Offer exclusive retailer SKUs and promotions\n• Co-create content with retailer blogs and newsletters\n• Track: sample-to-purchase conversion rates by location\n• Estimated reach: 50K+ samples monthly, 15% conversion to purchase"
    },
    {
      title: "Launch Referral Program with Customer Testimonial Campaigns",
      summary: "Turn satisfied customers into brand advocates with incentivized word-of-mouth marketing.",
      details: "• Offer $20 credit for both referrer and referee on first subscription\n• Create 'Share Your Story' campaign featuring real customer experiences\n• Build community platform for parents to share snack ideas and tips\n• Feature 'Customer of the Month' with family spotlight and free products\n• Develop toolkit for customers: social graphics, referral links, sample messages\n• Track: referral rates, lifetime value of referred customers\n• Estimated reach: 5K+ referrals monthly, viral customer testimonials"
    },
    {
      title: "Establish Thought Leadership Through PR and Media Relations",
      summary: "Position CEO and nutritionist as go-to experts for media coverage on organic snacking trends.",
      details: "• Pitch quarterly trend reports: 'State of Organic Snacking in America'\n• Offer expert commentary on health, sustainability, and family nutrition topics\n• Write op-eds for parenting publications and food industry media\n• Appear on podcasts: parenting, health, entrepreneurship shows\n• Secure TV segments: morning shows demonstrating healthy snack ideas\n• Sponsor industry conferences and speaking opportunities\n• Estimated reach: 10M+ media impressions quarterly, establish category authority"
    },
    {
      title: "Create School Partnership Programs for Nutrition Education",
      summary: "Build brand loyalty early by supporting schools with healthy snacking education programs.",
      details: "• Develop curriculum: 'Smart Snacking 101' for elementary schools\n• Offer free samples for classroom parties and PTA events\n• Sponsor school wellness programs and healthy eating challenges\n• Create teacher resource kits with nutrition education materials\n• Provide fundraising opportunities: percentage of sales back to schools\n• Host 'Farm to School' virtual field trips showing ingredient sourcing\n• Estimated reach: 100K+ families via school programs, community goodwill"
    }
  ],
  competitivePositioning: [
    {
      title: "NatureBox: Beat Them on Sustainability and Transparency",
      summary: "NatureBox leads in variety and customization. Win by emphasizing your superior ingredient transparency and eco-friendly packaging.",
      details: "• Their weakness: Plastic packaging, less transparent sourcing, some 'natural flavors'\n• Your advantage: 100% compostable packaging, farm-traceability, zero artificial anything\n• Messaging: 'NatureBox offers variety. Acme Snacks offers peace of mind.'\n• Content: Direct comparison showing packaging impact, ingredient sourcing transparency\n• Target: Environmentally conscious parents who scrutinize every ingredient\n• Estimated impact: Capture 25% of sustainability-focused buyers considering NatureBox"
    },
    {
      title: "Graze: Beat Them on Family-Friendly Options",
      summary: "Graze targets adult snackers. Own the family segment with kid-focused products and messaging.",
      details: "• Their weakness: Adult-centric flavors and marketing, no kid-specific options\n• Your advantage: Kid taste-tested products, family-sized portions, kid-friendly flavors\n• Messaging: 'Graze is for your snacking. Acme Snacks is for your whole family.'\n• Content: Family meal planning guides, kid lunchbox ideas, parent testimonials\n• Target: Parents shopping for family (not just themselves)\n• Estimated impact: Dominate 'family snacks' category where Graze doesn't compete"
    },
    {
      title: "Daily Harvest: Beat Them on Convenience and Value",
      summary: "Daily Harvest focuses on smoothies and frozen options. Win on shelf-stable convenience and better value per serving.",
      details: "• Their weakness: Freezer space required, higher price point, limited snack-specific options\n• Your advantage: Shelf-stable convenience, lower price per serving, snack-focused product line\n• Messaging: 'No freezer needed. Just grab and go healthy snacking.'\n• Content: Cost comparison calculator, convenience factor in busy family life\n• Target: Busy parents who want healthy options without meal prep or freezer management\n• Estimated impact: Capture families seeking convenient (not complicated) healthy snacking"
    },
    {
      title: "Generic Organic Brands (Whole Foods 365, Trader Joe's): Beat Them on Quality and Brand Trust",
      summary: "Store brands compete on price. Win on premium quality, brand story, and ingredient traceability.",
      details: "• Their weakness: Commodity positioning, no brand story, unknown sourcing\n• Your advantage: Premium ingredients, compelling origin story, farm partnerships, sustainable mission\n• Messaging: 'Not all organic is created equal. Acme Snacks means knowing exactly what you're feeding your family.'\n• Content: Farm stories, sourcing transparency, quality testing protocols\n• Target: Parents willing to pay premium for proven quality and transparency\n• Estimated impact: Justify 20-30% price premium over store brands with quality story"
    }
  ],
  topRecommendations: [
    {
      title: "Launch 'Farm Partner Transparency Initiative' Marketing Campaign",
      summary: "Differentiate through radical transparency - show exactly where every ingredient comes from with verifiable farm partnerships.",
      details: "• Build interactive ingredient traceability map on website\n• Film video series visiting farm partners and showing sourcing process\n• Create quarterly transparency reports with third-party audits\n• Offer 'Meet Your Farmer' events (virtual and in-person)\n• Add QR codes to packaging linking to ingredient source information\n• Partner with Good Food Institute or similar org for credibility\n• Timeline: 3 months to launch, ongoing content creation\n• Budget: $50K initial build, $10K/month maintenance\n• Expected ROI: Position as most transparent brand, +15% premium perception"
    },
    {
      title: "Create Strategic Content Partnerships with 5 High-Authority Health Publications",
      summary: "AI assistants cite Healthline, WebMD, Food Navigator, Forbes, and Parents Magazine most. Get featured in all five.",
      details: "• Develop unique story angles for each publication's audience\n• Offer exclusive data, research, or expert access they can't get elsewhere\n• Co-create content: guides, reports, research studies\n• Provide executive commentary on industry trends and health topics\n• Sponsor relevant content verticals (family nutrition, sustainable food)\n• Timeline: 6 months to establish all partnerships\n• Budget: $25K/partnership (product samples, co-marketing, events)\n• Expected ROI: 15-20 authoritative citations, +25% AI mention rate for health queries"
    },
    {
      title: "Build Comprehensive 'Organic Snacking Education Hub' Resource Center",
      summary: "Become the definitive educational resource on organic snacking - capture all educational search intent.",
      details: "• Create 20+ pillar content pieces: buying guides, nutrition comparisons, ingredient glossaries\n• Develop interactive tools: nutrition calculator, snack finder, meal planner\n• Publish original research: 'State of Organic Snacking Report' with survey data\n• Build resource library: downloadable PDFs, meal plans, shopping guides\n• Optimize all content with schema markup for AI parsing\n• Timeline: 4 months to build core hub, ongoing content expansion\n• Budget: $40K content creation, $15K/month ongoing\n• Expected ROI: Rank #1 for 25+ educational queries, establish thought leadership"
    },
    {
      title: "Develop 'Acme Snacks vs Competitors' Comparison Landing Pages",
      summary: "27 queries directly compare you to competitors but you have zero comparison content. Critical gap to fill immediately.",
      details: "• Create dedicated pages: Acme vs NatureBox, Acme vs Graze, Acme vs Daily Harvest\n• Include: side-by-side feature comparison, nutritional analysis, pricing, sustainability metrics\n• Add customer testimonials from switchers: 'Why I switched from [competitor] to Acme'\n• Optimize for comparison keywords and long-tail variations\n• Keep updated monthly with current pricing and product changes\n• Timeline: 1 month to launch all comparison pages\n• Budget: $15K (copywriting, design, ongoing updates)\n• Expected ROI: Capture 70% of direct comparison queries (currently 22%), direct revenue impact"
    }
  ]
}

// Executive Insights - Dashboard summary combining search and market intelligence
export const demoExecutiveInsights = {
  summary: "Acme Snacks shows strong potential in AI visibility with a 67% mention rate in discovery queries, but faces critical gaps in purchase-intent and comparison content. Your brand excels at educational content but competitors dominate transactional searches. The primary opportunity is converting your top-of-funnel awareness into bottom-funnel conversions through strategic comparison and pricing content.",
  searchHighlights: [
    "Leading in discovery queries (67% mention rate) - strong brand awareness foundation",
    "Critical gap: Only 43% mention rate for purchase-intent queries vs competitors' 78%",
    "Missing from 27 direct competitor comparison queries - high-intent traffic loss",
    "Strong authority potential: 5 key publications could add 15-20 AI citations"
  ],
  marketHighlights: [
    "Clear positioning opportunity: Premium family-focused organic snacks with transparency",
    "Competitor weakness identified: NatureBox lacks sustainability story, Graze ignores families",
    "Value proposition gap: Ingredient transparency underutilized in current messaging",
    "Target audience clarity: Health-conscious parents (30-45) seeking convenient guilt-free options"
  ],
  keyRecommendations: [
    "Create comparison landing pages for top 3 competitors to capture high-intent search traffic",
    "Launch Farm Partner Transparency Initiative to differentiate from commodity organic brands",
    "Build comprehensive pricing and subscription content to improve purchase-intent mentions",
    "Partner with Healthline and Parents Magazine for authoritative citation building"
  ]
}
